import React, { useEffect, useState } from "react";
import { ResponsiveLine } from "@nivo/line";

const WeeklyActivityTrendChart = ({ patientData }) => {
  const [chartData, setChartData] = useState([]);
  const [dailyAverages, setDailyAverages] = useState({});
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(1);
  const [weeklyAverage, setWeeklyAverage] = useState(0);

  const turquoiseColor = "#06b6d4";
  const fillColor = "rgba(6, 182, 212, 0.2)";
  const redColor = "#ef4444"; // Red color for weekly average

  useEffect(() => {
    if (
      patientData &&
      patientData.month_steps &&
      patientData.month_steps.length >= 2
    ) {
      const stepsData = patientData.month_steps[0] || [];
      const datesData = patientData.month_steps[1] || [];

      const allData = datesData
        .map((dateStr, index) => {
          const date = new Date(dateStr);
          return {
            date,
            dateStr,
            day: date.getDay(),
            steps: stepsData[index] || 0,
          };
        })
        .filter((item) => !isNaN(item.date.getTime()));

      allData.sort((a, b) => a.date - b.date);

      const dayTotals = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      const dayCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

      allData.forEach((item) => {
        if (item.steps > 0) {
          dayTotals[item.day] += item.steps;
          dayCounts[item.day]++;
        }
      });

      const dailyAvgs = {};
      for (let i = 0; i < 7; i++) {
        dailyAvgs[i] =
          dayCounts[i] > 0 ? Math.round(dayTotals[i] / dayCounts[i]) : 0;
      }

      setDailyAverages(dailyAvgs);

      const weeks = [];
      let currentWeek = [];
      let currentWeekStart = null;

      allData.forEach((item) => {
        if (
          currentWeekStart === null ||
          (item.day === 0 && currentWeek.length > 0)
        ) {
          if (currentWeek.length > 0) {
            weeks.push({ startDate: currentWeekStart, data: [...currentWeek] });
          }
          currentWeekStart = new Date(item.date);
          currentWeek = [item];
        } else {
          currentWeek.push(item);
        }
      });

      if (currentWeek.length > 0) {
        weeks.push({ startDate: currentWeekStart, data: [...currentWeek] });
      }

      const formattedWeeks = weeks.map((week) => {
        const startDate = week.startDate;
        const endDateMs = new Date(startDate).setDate(startDate.getDate() + 6);
        const endDate = new Date(endDateMs);

        return {
          label: `${startDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} - ${endDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`,
          startDate,
          endDate,
          data: week.data,
        };
      });

      setAvailableWeeks(formattedWeeks.reverse());

      if (formattedWeeks.length > 0) {
        updateChartWithWeek(formattedWeeks[0], dailyAvgs);
      }
    }
  }, [patientData]);

  useEffect(() => {
    if (
      availableWeeks.length > 0 &&
      selectedWeekIndex >= 0 &&
      selectedWeekIndex < availableWeeks.length
    ) {
      updateChartWithWeek(availableWeeks[selectedWeekIndex], dailyAverages);
    }
  }, [selectedWeekIndex, availableWeeks, dailyAverages]);

  const updateChartWithWeek = (week, dayAverages) => {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const fullWeek = {};
    for (let i = 0; i < 7; i++) {
      fullWeek[i] = {
        day: i,
        dayName: dayNames[i],
        steps: 0,
        hasData: false,
      };
    }

    week.data.forEach((day) => {
      fullWeek[day.day] = {
        day: day.day,
        dayName: dayNames[day.day],
        date: day.date,
        dateStr: day.dateStr,
        steps: day.steps,
        hasData: true,
      };
    });

    const weekData = Object.values(fullWeek).sort((a, b) => a.day - b.day);

    // Calculate weekly average
    const dataPointsWithSteps = weekData.filter((day) => day.hasData);
    const totalSteps = dataPointsWithSteps.reduce(
      (sum, day) => sum + day.steps,
      0
    );
    const avgSteps =
      dataPointsWithSteps.length > 0
        ? Math.round(totalSteps / dataPointsWithSteps.length)
        : 0;

    setWeeklyAverage(avgSteps);

    // console.log(dayAverages)

    const nivoData = [
      {
        id: "Activity",
        data: weekData.map((day) => ({
          x: day.dayName,
          y: day.hasData ? day.steps : null,
          date: day.date,
          fullDate: day.date
            ? day.date.toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : `${day.dayName} (No data)`,
          hasData: day.hasData,
          dayAverage: dayAverages[day.day],
          compared:
            day.hasData && dayAverages[day.day] > 0
              ? Math.round((day.steps / dayAverages[day.day]) * 100)
              : 100,
        })),
      },
    ];

    setChartData(nivoData);
  };

  const handleWeekChange = (e) => {
    const index = parseInt(e.target.value, 10);
    setSelectedWeekIndex(index);
  };

  const theme = {
    background: "transparent",
    textColor: "#333333",
    fontSize: 12,
    axis: {
      domain: { line: { stroke: "#dddddd", strokeWidth: 1 } },
      ticks: {
        line: { stroke: "#dddddd", strokeWidth: 1 },
        text: { fill: "#666666", fontSize: 11 },
      },
      legend: { text: { fill: "#666666", fontSize: 13 } },
    },
    grid: { line: { stroke: "#eeeeee", strokeWidth: 1 } },
    tooltip: {
      container: {
        background: "#ffffff",
        fontSize: 12,
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        padding: "8px 10px",
      },
    },
  };

  const CustomTooltip = ({ point }) => {
    if (!point.data.hasData) {
      return (
        <div
          style={{
            padding: "10px 12px",
            background: "#ffffff",
            border: "1px solid #ccc",
            borderRadius: "3px",
            minWidth: "200px",
            color: "black",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
            {point.data.x}
          </div>
          <div>No data available</div>
        </div>
      );
    }

    let comparisonText = "";
    if (point.data.dayAverage > 0) {
      if (point.data.compared > 100) {
        comparisonText = `${
          point.data.compared - 100
        }% more steps than the average ${point.data.x}`;
      } else if (point.data.compared < 100) {
        comparisonText = `${
          100 - point.data.compared
        }% fewer steps than the average ${point.data.x}`;
      } else {
        comparisonText = `Same as the average for ${point.data.x}`;
      }
    }

    return (
      <div
        style={{
          padding: "10px 12px",
          background: "#ffffff",
          border: "1px solid #ccc",
          borderRadius: "3px",
          minWidth: "200px",
          color: "black",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {point.data.fullDate}
        </div>
        <div style={{ color: "black", marginBottom: "6px" }}>
          Steps: <strong>{point.data.y.toLocaleString()}</strong>
        </div>
        {point.data.dayAverage > 0 && (
          <div
            style={{
              fontSize: "12px",
              paddingTop: "6px",
              color: "black",
            }}
          >
            {comparisonText}
          </div>
        )}
        <div style={{ fontSize: "12px", paddingTop: "6px", color: "black" }}>
          Weekly Avg: <strong>{weeklyAverage.toLocaleString()}</strong> steps
        </div>
      </div>
    );
  };

  const selectStyle = {
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    fontSize: "14px",
    color: "#333",
    width: "100%",
    maxWidth: "350px",
    marginBottom: "0px",
    marginTop: "30px",
    cursor: "pointer",
  };

  if (availableWeeks.length === 0) {
    return (
      <div className="graph">
        <h3>Weekly Activity Trend</h3>
        <div style={{ padding: "20px 0", color: "#666", textAlign: "center" }}>
          Not enough data to display weekly activity.
        </div>
      </div>
    );
  }

  const calculateWeekMetrics = () => {
    if (chartData.length === 0 || !chartData[0].data)
      return { mostActive: null, average: 0, trend: null };

    const dataPoints = chartData[0].data.filter((day) => day.hasData);
    if (dataPoints.length === 0)
      return { mostActive: null, average: 0, trend: null };

    const mostActive = [...dataPoints].sort((a, b) => b.y - a.y)[0];
    const average = Math.round(
      dataPoints.reduce((sum, day) => sum + day.y, 0) / dataPoints.length
    );

    let trend = null;
    if (dataPoints.length >= 4) {
      const firstHalf = dataPoints.slice(0, Math.ceil(dataPoints.length / 2));
      const secondHalf = dataPoints.slice(Math.ceil(dataPoints.length / 2));

      const firstHalfAvg =
        firstHalf.reduce((sum, d) => sum + d.y, 0) / firstHalf.length;
      const secondHalfAvg =
        secondHalf.reduce((sum, d) => sum + d.y, 0) / secondHalf.length;

      const percentChange =
        firstHalfAvg > 0
          ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100
          : 0;

      trend = {
        direction:
          percentChange > 5
            ? "increasing"
            : percentChange < -5
            ? "decreasing"
            : "stable",
        percentChange: Math.abs(Math.round(percentChange)),
        color:
          percentChange > 5
            ? "#22c55e"
            : percentChange < -5
            ? "#ef4444"
            : "#6b7280",
      };
    }

    return { mostActive, average, trend };
  };

  const weekMetrics = calculateWeekMetrics();

  return (
    <div className="graph">
      <h3>Weekly Activity Trend</h3>
      <div style={{ marginBottom: "20px" }}>
        <select
          value={selectedWeekIndex}
          onChange={handleWeekChange}
          style={selectStyle}
          aria-label="Select week"
        >
          {availableWeeks.map((week, index) => (
            <option key={index} value={index}>
              {week.label}
            </option>
          ))}
        </select>

        {/* Added Weekly Average Display */}
        <div
          style={{
            marginTop: "10px",
            fontSize: "15px",
            fontWeight: "bold",
            color: redColor,
          }}
        >
          Week Average: {weeklyAverage.toLocaleString()} steps
        </div>
      </div>

      <div style={{ height: "350px" }}>
        <ResponsiveLine
          data={chartData}
          margin={{ top: 40, right: 50, bottom: 50, left: 60 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto" }}
          curve="cardinal"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Day of Week",
            legendOffset: 40,
            legendPosition: "middle",
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Steps",
            legendOffset: -50,
            legendPosition: "middle",
            format: (value) => value.toLocaleString(),
          }}
          colors={[turquoiseColor]}
          pointSize={10}
          pointColor="grey"
          pointBorderColor={turquoiseColor}
          enablePointLabel={false}
          enableGridX={false}
          enableGridY={true}
          enableArea={true}
          areaBaselineValue={0}
          areaOpacity={0.2}
          areaColor={fillColor}
          areaBlendMode="normal"
          useMesh={true}
          theme={theme}
          tooltip={CustomTooltip}
          lineWidth={3}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          enableSlices={false}
          legends={[]}
          markers={[
            {
              axis: "y",
              value: weeklyAverage,
              lineStyle: {
                stroke: redColor,
                strokeWidth: 2,
                strokeDasharray: "5, 5",
              },
              legend: `Avg: ${weeklyAverage.toLocaleString()} steps`,
              legendOrientation: "horizontal",
              legendPosition: "top-right",
              textStyle: { fill: redColor, fontSize: 14, fontWeight: "bold" },
            },
          ]}
        />
      </div>
    </div>
  );
};

export default WeeklyActivityTrendChart;
